import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  CheckCircleOutline as CheckIcon,
  Email as EmailIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import MillosLogo from '../components/MillosLogo';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

type Status = 'processing' | 'success' | 'error';

const GoogleAuthCallback: React.FC = () => {
  const { token } = useAuth();
  const { palette } = useTheme();
  const t = palette.tokens;
  const [status, setStatus] = useState<Status>('processing');
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isProcessing = useRef(false);
  // Opened from the app in a new tab (or directly) -> go back to the app; opened as a popup -> close it
  const [isNewTab] = useState(() => window.history.length === 1 || !window.opener);

  useEffect(() => {
    const handleCallback = async () => {
      if (isProcessing.current) return;
      isProcessing.current = true;

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const oauthError = urlParams.get('error');

        if (oauthError) {
          throw new Error(`OAuth error: ${oauthError}`);
        }

        if (!code || !state) {
          throw new Error('Missing authorization code or state parameter');
        }

        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
          throw new Error('Please log in first');
        }

        // Send the authorization code to our backend
        const response = await fetch(`${config.apiUrl}/api/auth/google/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            code: code,
            state: state,
            redirect_uri: `${window.location.origin}/auth/google/callback`
          })
        });

        const data = await response.json();

        if (data.success) {
          setStatus('success');
          setGoogleEmail(data.google_email);

          if (isNewTab) {
            setTimeout(() => {
              window.location.href = window.location.origin;
            }, 3000);
          } else {
            setTimeout(() => {
              window.close();
            }, 10000);
          }
        } else {
          throw new Error(data.error || 'Failed to complete authentication');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Authentication failed');

        setTimeout(() => {
          if (isNewTab) {
            window.location.href = window.location.origin;
          } else {
            window.close();
          }
        }, 3000);
      }
    };

    handleCallback();
  }, [token, isNewTab]);

  const title = {
    processing: 'Connecting Google',
    success: 'Google connected',
    error: "Couldn't connect Google",
  }[status];

  const unlocked = [
    { icon: <EmailIcon sx={{ fontSize: 18 }} />, name: 'Gmail', desc: 'Renewal certificates from your providers are picked up automatically.' },
    { icon: <CalendarIcon sx={{ fontSize: 18 }} />, name: 'Calendar', desc: 'Expiry reminders are added to your calendar.' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: t.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box
        role="status"
        aria-live="polite"
        sx={{ width: '100%', maxWidth: 440, bgcolor: '#fff', border: `1px solid ${t.hairline}`, borderRadius: '16px', p: { xs: 3, sm: 4 }, textAlign: 'center' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <MillosLogo size={30} wordmark />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {status === 'processing' && <CircularProgress size={44} sx={{ color: t.primary }} />}
          {status === 'success' && <CheckIcon sx={{ fontSize: 52, color: t.success }} />}
          {status === 'error' && <ErrorIcon sx={{ fontSize: 52, color: t.error }} />}
        </Box>

        <Typography component="h1" sx={{ fontSize: '22px', fontWeight: 600, color: t.ink, mb: 0.5 }}>{title}</Typography>

        {status === 'processing' && (
          <Typography sx={{ fontSize: '15px', color: t.muted }}>Finishing the connection. This only takes a moment.</Typography>
        )}

        {status === 'success' && (
          <>
            <Typography sx={{ fontSize: '15px', color: t.muted, mb: 3, wordBreak: 'break-word' }}>
              Connected as <Box component="span" sx={{ color: t.ink, fontWeight: 500 }}>{googleEmail}</Box>
            </Typography>

            <Box sx={{ textAlign: 'left', borderTop: `1px solid ${t.hairline}`, pt: 2, mb: 3 }}>
              {unlocked.map(item => (
                <Box key={item.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: t.surfaceSoft, color: t.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} aria-hidden>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: t.ink }}>{item.name}</Typography>
                    <Typography sx={{ fontSize: '14px', color: t.muted }}>{item.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {status === 'error' && (
          <Typography sx={{ fontSize: '15px', color: t.muted, mb: 3, wordBreak: 'break-word' }}>
            {error} Please try again, and contact support if it keeps happening.
          </Typography>
        )}

        {status !== 'processing' && (
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" disableElevation onClick={() => { window.location.href = window.location.origin; }} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Back to MillOS
            </Button>
            {!isNewTab && (
              <Button variant="outlined" onClick={() => window.close()} sx={{ textTransform: 'none', fontWeight: 500 }}>
                Close this tab
              </Button>
            )}
          </Box>
        )}

        {status !== 'processing' && (
          <Typography sx={{ fontSize: '13px', color: t.muted, mt: 2 }}>
            {isNewTab ? 'Taking you back to MillOS in a moment.' : 'This tab will close by itself, or you can close it now.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default GoogleAuthCallback;
